import React, { useCallback, useEffect, useState } from "react";
import EmptyNotice from "./EmptyNotice";
import { fetchPatientConversationIds } from "../../utils/conversationIds";
import { environment } from "../../config/environment";

const ConversationIdFinder = ({
  patientId,
  onSelectConversation,
  actionLabel = "Use conversation",
  actionLoadingId = null,
  selectedConversationId = null,
  description = `Loads unique conversation IDs from ${environment.realtimeAudioChunksTable} for this patient.`,
  refreshLabel = "Refresh conversations",
  emptyMessage,
  refreshButtonClassName = "inline-flex items-center px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed",
  actionButtonClassName = "inline-flex items-center px-3 py-1.5 text-xs bg-gray-900 text-white rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed",
  className = "",
}) => {
  const [conversations, setConversations] = useState([]);
  const [lookupError, setLookupError] = useState(null);
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [hasLookedUp, setHasLookedUp] = useState(false);

  const runLookup = useCallback(async () => {
    const trimmedPatientId = patientId?.trim?.() || patientId;

    if (!trimmedPatientId) {
      setLookupError("Patient ID is required.");
      setConversations([]);
      setHasLookedUp(true);
      return;
    }

    setIsLookingUp(true);
    setLookupError(null);
    setHasLookedUp(true);

    const result = await fetchPatientConversationIds(trimmedPatientId);

    setIsLookingUp(false);

    if (!result.success) {
      setConversations([]);
      setLookupError(result.error || "Failed to load conversations.");
      return;
    }

    setConversations(result.conversations);
  }, [patientId]);

  useEffect(() => {
    runLookup();
  }, [runLookup]);

  return (
    <div className={`space-y-3 ${className}`}>
      {description && <p className="text-xs text-gray-600">{description}</p>}

      <button
        type="button"
        onClick={runLookup}
        disabled={isLookingUp || !patientId?.trim?.()}
        className={refreshButtonClassName}
      >
        {isLookingUp ? "Loading…" : refreshLabel}
      </button>

      {lookupError && <p className="text-xs text-red-600">{lookupError}</p>}

      {hasLookedUp && !isLookingUp && !lookupError && conversations.length === 0 && (
        <EmptyNotice>
          {emptyMessage ||
            `No audio chunk conversations found for patient ${patientId}.`}
        </EmptyNotice>
      )}

      {conversations.length > 0 && (
        <div className="overflow-x-auto border border-gray-200 rounded-lg">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr className="border-b border-gray-200">
                <th className="text-left py-2 px-3 font-medium text-gray-700">
                  Conversation ID
                </th>
                <th className="text-left py-2 px-3 font-medium text-gray-700">
                  Chunks
                </th>
                <th className="text-left py-2 px-3 font-medium text-gray-700">
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {conversations.map((row) => {
                const isSelected = selectedConversationId === row.conversation_id;
                const isLoading = actionLoadingId === row.conversation_id;

                return (
                  <tr
                    key={row.conversation_id}
                    className={`border-b border-gray-100 last:border-b-0 ${
                      isSelected ? "bg-blue-50" : ""
                    }`}
                  >
                    <td className="py-2 px-3 font-mono text-xs text-gray-900 break-all">
                      {row.conversation_id}
                    </td>
                    <td className="py-2 px-3 text-gray-700 whitespace-nowrap">
                      {row.total_chunks}
                    </td>
                    <td className="py-2 px-3 whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => onSelectConversation?.(row.conversation_id)}
                        disabled={Boolean(actionLoadingId) || !onSelectConversation}
                        className={actionButtonClassName}
                      >
                        {isLoading ? "Working…" : isSelected ? "Selected" : actionLabel}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ConversationIdFinder;
